import { api } from '@/lib/api'
import type { DataEnvelope, Media, UploadIntent, UploadPartUrl } from '@/types/api'
import { hashFile } from './hash'
import { PartUploadError, type IntentBody, type IntentResult, type PartTarget, type UploadDeps } from './manager'

/**
 * PUT bytes straight to object storage with XHR (upload progress events).
 *
 * Storage URLs are presigned and usually cross-origin: no cookies
 * (`withCredentials = false`) and no XSRF / app headers — only the headers the
 * server signed into the part (`parts[n].headers`).
 */
export function xhrPut(target: PartTarget, body: Blob, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<{ etag: string | null }> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
    const xhr = new XMLHttpRequest()
    xhr.open(target.method || 'PUT', target.url, true)
    xhr.withCredentials = false
    for (const [k, v] of Object.entries(target.headers ?? {})) {
      // The browser owns these; setting them throws.
      if (/^(host|content-length|connection)$/i.test(k)) continue
      xhr.setRequestHeader(k, v)
    }
    const onAbort = () => xhr.abort()
    signal.addEventListener('abort', onAbort, { once: true })
    const cleanup = () => signal.removeEventListener('abort', onAbort)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded)
    }
    xhr.onload = () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(body.size)
        resolve({ etag: xhr.getResponseHeader('ETag') })
      } else {
        reject(new PartUploadError(xhr.status))
      }
    }
    xhr.onerror = () => {
      cleanup()
      reject(new PartUploadError(0))
    }
    xhr.ontimeout = () => {
      cleanup()
      reject(new PartUploadError(0, 'Upload timed out'))
    }
    xhr.onabort = () => {
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }
    xhr.send(body)
  })
}

export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'))
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** `201 {data: UploadIntent}` vs `200 {data: {duplicate_of: Media}}`. */
export function parseIntentResponse(res: DataEnvelope<UploadIntent | { duplicate_of: Media }>): IntentResult {
  const data = res.data as Partial<UploadIntent> & { duplicate_of?: Media }
  if (data && data.duplicate_of && typeof data.duplicate_of === 'object' && !data.upload_id) return { type: 'duplicate', media: data.duplicate_of }
  return { type: 'intent', intent: data as UploadIntent }
}

/** Real network implementation of the manager's dependencies. */
export const httpUploadDeps: UploadDeps = {
  async createIntent(body: IntentBody, replaceOf: string | null) {
    const path = replaceOf ? `/media/${replaceOf}/replace` : '/media/uploads'
    return parseIntentResponse(await api.post<DataEnvelope<UploadIntent | { duplicate_of: Media }>>(path, body))
  },
  async requestPartUrls(uploadId, partNumbers) {
    const res = await api.post<DataEnvelope<{ parts: UploadPartUrl[] }>>(`/media/uploads/${uploadId}/parts`, { part_numbers: partNumbers })
    return res.data.parts
  },
  async complete(uploadId, parts) {
    return (await api.post<DataEnvelope<Media>>(`/media/uploads/${uploadId}/complete`, { parts })).data
  },
  async abort(uploadId) {
    await api.delete(`/media/uploads/${uploadId}`)
  },
  async getMedia(id) {
    return (await api.get<DataEnvelope<Media>>(`/media/${id}`)).data
  },
  async getIntent(uploadId) {
    return (await api.get<DataEnvelope<UploadIntent>>(`/media/uploads/${uploadId}`)).data
  },
  putPart: xhrPut,
  hash: hashFile,
  sleep: abortableSleep,
  now: () => Date.now(),
}
